import dayjs from "dayjs";
import React, { FC, useEffect } from "react";
import { DatePicker, Modal, TextArea } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useNotify } from "~/shared/hooks/useNotify";
import { DISABLED_FUTURE_DATE } from "~/shared/lib/disabledFutureDate";

import s from "./EmployeeDismissModal.less";

interface IEmployeeDismissForm {
  dismissDate: dayjs.Dayjs | null;
  comment: string;
}

interface IEmployeeDismissModalProps {
  selectedCount: number;
  onSuccess?: () => void;
}

const EmployeeDismissModal: FC<IEmployeeDismissModalProps> = ({
  selectedCount,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { openModal } = useURLParamsGetter<{ openModal?: string }>();
  const setParams = useURLParamsSetter();
  const { notifySuccess } = useNotify();

  const isOpen = openModal === "Dismiss";

  const { control, watch, reset } = useForm<IEmployeeDismissForm>({
    defaultValues: {
      dismissDate: null,
      comment: "",
    },
  });

  const dismissDateValue = watch("dismissDate");

  const closeModal = () => {
    setParams({ openModal: undefined });
  };

  useEffect(() => {
    if (!isOpen) {
      reset({ dismissDate: null, comment: "" });
    }
  }, [isOpen, reset]);

  const handleSubmit = () => {
    // TODO(PASDILER-1952): мутация массового увольнения, когда появится API
    notifySuccess(t("employee.dismissSuccess"));
    closeModal();
    onSuccess?.();
  };

  return (
    <Modal
      open={isOpen}
      title={t("employee.dismissReasonTitle")}
      onCancel={closeModal}
      centered
      size="small"
      cancelButton={{ children: t("employee.cancelAndClose") }}
      okButton={{
        children: t("ui.save"),
        disabled: !selectedCount || !dismissDateValue,
        onClick: handleSubmit,
      }}
    >
      <div className={s.form}>
        <div className={s.field}>
          <span className={s.label}>{t("employee.dismissDate")}</span>
          <Controller
            name="dismissDate"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <DatePicker
                placeholder={t("employee.dismissDate")}
                format="DD.MM.YYYY"
                value={value}
                onBlur={onBlur}
                onChange={(date) => onChange(date ?? null)}
                disabledDate={DISABLED_FUTURE_DATE}
              />
            )}
          />
        </div>

        <Controller
          name="comment"
          control={control}
          render={({ onChange, value, onBlur }) => (
            <TextArea
              value={value}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              placeholder={t("employee.dismissCommentPlaceholder")}
              rows={4}
            />
          )}
        />
      </div>
    </Modal>
  );
};

export default EmployeeDismissModal;
